import React, { useEffect, useRef, useState } from 'react';
import { Wifi, WifiOff, RefreshCw, CheckCircle } from 'lucide-react';
import { useOffline } from '@/contexts/OfflineContext';

export default function OfflineBanner() {
  const { isOnline, isSyncing, pendingCount, triggerSync } = useOffline();
  const [showSynced, setShowSynced] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const prevSyncing = useRef(false);

  useEffect(() => {
    if (prevSyncing.current && !isSyncing && isOnline && pendingCount === 0) {
      setShowSynced(true);
      setDismissed(false);
      const t = setTimeout(() => setShowSynced(false), 4000);
      return () => clearTimeout(t);
    }
    prevSyncing.current = isSyncing;
  }, [isSyncing, isOnline, pendingCount]);

  // Reset dismissed state when there are new pending items
  useEffect(() => {
    if (pendingCount > 0) setDismissed(false);
  }, [pendingCount]);

  if (!isOnline) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs">
        <WifiOff className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium">You're offline.</span>
        <span>Billing still works.</span>
        {pendingCount > 0 && (
          <span className="ml-1 font-medium">{pendingCount} sale{pendingCount !== 1 ? 's' : ''} will sync when connected.</span>
        )}
      </div>
    );
  }

  if (isSyncing) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-200 text-blue-800 text-xs">
        <RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin" />
        <span>Back online — syncing offline sales…</span>
        {pendingCount > 0 && <span className="font-medium">{pendingCount} remaining</span>}
      </div>
    );
  }

  if (showSynced && !dismissed) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border-b border-emerald-200 text-emerald-800 text-xs">
        <CheckCircle className="h-3.5 w-3.5 shrink-0" />
        <span>All offline sales synced successfully.</span>
        <button
          onClick={() => setDismissed(true)}
          className="ml-auto text-emerald-600 hover:text-emerald-800 font-medium"
        >
          Dismiss
        </button>
      </div>
    );
  }

  if (isOnline && pendingCount > 0 && !dismissed) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs">
        <Wifi className="h-3.5 w-3.5 shrink-0" />
        <span>{pendingCount} offline sale{pendingCount !== 1 ? 's' : ''} pending sync.</span>
        <button
          onClick={triggerSync}
          className="ml-2 underline font-medium hover:text-amber-900"
        >
          Sync now
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="ml-auto text-amber-600 hover:text-amber-800"
        >
          ✕
        </button>
      </div>
    );
  }

  return null;
}
